import React, { useState, useEffect } from 'react';
import { XMarkIcon, UserPlusIcon, UserMinusIcon, ShieldCheckIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { useClassrooms } from '../hooks/useClassrooms';
import { useAuth } from '../contexts/AuthContext';
import { useUsers } from '../hooks/useUsers';
import { Classroom } from '../types';
import ConfirmationModal from './ConfirmationModal';

interface MemberManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  classroom: Classroom;
}

const MemberManagementModal: React.FC<MemberManagementModalProps> = ({ isOpen, onClose, classroom }) => {
  const { addMember, removeMember, addAdmin, removeAdmin, isAdmin } = useClassrooms();
  const { currentUser } = useAuth();
  const { users, fetchUsers, getUserDisplayName, getUserAvatar } = useUsers();
  
  // Fetch user data when modal opens
  useEffect(() => {
    if (isOpen && classroom.members.length > 0) {
      fetchUsers(classroom.members);
    }
  }, [isOpen, classroom.members, fetchUsers]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    confirmButtonColor?: 'red' | 'blue' | 'green';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmText: 'Confirm',
    confirmButtonColor: 'red'
  });

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMemberEmail.trim()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await addMember(classroom.id!, newMemberEmail.trim());
      setSuccess('Member added successfully!');
      setNewMemberEmail('');
    } catch (err) {
      setError('Failed to add member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Remove Member',
      message: 'Are you sure you want to remove this member? This action cannot be undone.',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        setLoading(true);
        setError('');
        setSuccess('');

        try {
          await removeMember(classroom.id!, memberId);
          setSuccess('Member removed successfully!');
        } catch (err) {
          setError('Failed to remove member. Please try again.');
        } finally {
          setLoading(false);
        }
      },
      confirmText: 'Remove Member',
      confirmButtonColor: 'red'
    });
  };

  const handleAddAdmin = async (memberId: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Make Admin',
      message: 'Are you sure you want to make this member an admin? They will have full control over the classroom.',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        setLoading(true);
        setError('');
        setSuccess('');

        try {
          await addAdmin(classroom.id!, memberId);
          setSuccess('Admin added successfully!');
        } catch (err) {
          setError('Failed to add admin. Please try again.');
        } finally {
          setLoading(false);
        }
      },
      confirmText: 'Make Admin',
      confirmButtonColor: 'blue'
    });
  };

  const handleRemoveAdmin = async (adminId: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Remove Admin',
      message: 'Are you sure you want to remove admin privileges from this user? They will lose administrative access.',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        setLoading(true);
        setError('');
        setSuccess('');

        try {
          await removeAdmin(classroom.id!, adminId);
          setSuccess('Admin privileges removed successfully!');
        } catch (err) {
          setError('Failed to remove admin privileges. Please try again.');
        } finally {
          setLoading(false);
        }
      },
      confirmText: 'Remove Admin',
      confirmButtonColor: 'red'
    });
  };

  const canManageMembers = currentUser && isAdmin(classroom, currentUser.uid);
  const hasAdmins = classroom.admins && Array.isArray(classroom.admins);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Manage Members</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {!canManageMembers && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  Only classroom admins can manage members.
                </p>
              </div>
            )}

            {/* Add New Member */}
            {canManageMembers && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Add New Member</h4>
                <form onSubmit={handleAddMember} className="flex space-x-3">
                  <input
                    type="email"
                    value={newMemberEmail}
                    onChange={(e) => setNewMemberEmail(e.target.value)}
                    placeholder="Enter member email"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                  <button
                    type="submit"
                    disabled={loading || !newMemberEmail.trim()}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    <UserPlusIcon className="h-4 w-4 mr-2" />
                    Add
                  </button>
                </form>
              </div>
            )}

            {/* Messages */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}
            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-sm">{success}</p>
              </div>
            )}

            {/* Members List */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-900">Current Members ({classroom.memberCount})</h4>
              
              <div className="max-h-96 overflow-y-auto space-y-2">
                {classroom.members.map((memberId, index) => {
                  const isMemberAdmin = (classroom.admins && classroom.admins.includes(memberId)) || classroom.createdBy === memberId;
                  const isCurrentUser = currentUser?.uid === memberId;
                  const displayName = getUserDisplayName(memberId);
                  const avatar = getUserAvatar(memberId);
                  const isCreator = classroom.createdBy=== memberId;
                  
                  return (
                    <div key={memberId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          {avatar ? (
                            <img
                              src={avatar}
                              alt={displayName}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-xs font-medium">
                              {displayName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          
                          {/* Star for creator */}
                          {isCreator && (
                            <div className="absolute -top-1 -right-1">
                              <StarIcon className="h-4 w-4 text-yellow-500 drop-shadow-sm" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 flex items-center">
                            {displayName}
                            {isMemberAdmin && !isCreator && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                <ShieldCheckIcon className="h-3 w-3 mr-1" />
                                Admin
                              </span>
                            )}
                            {isCreator && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                <StarIcon className="h-3 w-3 mr-1" />
                                Creator
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500">
                            {isCreator ? 'Classroom Creator' : isMemberAdmin ? 'Administrator' : 'Member'}
                          </p>
                        </div>
                      </div>
                      
                      {canManageMembers && !isCurrentUser && (
                        <div className="flex items-center space-x-2">
                          {isMemberAdmin ? (
                            <button
                              onClick={() => handleRemoveAdmin(memberId)}
                              disabled={loading}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove admin privileges"
                            >
                              <ShieldExclamationIcon className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleAddAdmin(memberId)}
                              disabled={loading}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Make admin"
                            >
                              <ShieldCheckIcon className="h-4 w-4" />
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleRemoveMember(memberId)}
                            disabled={loading}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove member"
                          >
                            <UserMinusIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmButtonColor={confirmModal.confirmButtonColor}
      />
    </div>
  );
};

export default MemberManagementModal;
